import { apiThreadPool } from "@/cache/SpatioTemporalTileCache/ApiQueryThreadPool";
import { aviationLayerConfig } from "@/constants/layerConfigAttributes";
import type { MapboxGeoJSONFeature, Style } from "mapbox-gl";
import { BaseSubLayer } from "./BaseSubLayer";
import { ICON_LAYERS, INTERACTIVE_LAYERS_NAMES, TEXT_LAYERS, composeMetarLayers } from "./metarLayerCompositor";
import { createMetarTooltip } from "./tooltips/metarTooltip";
import { convertRange, showTimeOutFeature } from "./utils";

const METAR_TIME_SPAN = 24 * 5;

export class MetarLayer extends BaseSubLayer {
  createLayers(_style: Style): void {
    const map = this.scene.getMapboxMap();

    const metarVectorLayers = composeMetarLayers(
      `${this.uuid}_tiles`,
      this.initialProps.text_size,
      this.initialProps.show,
      this.initialProps.custom_options.show_timed_out ?? aviationLayerConfig.defaultMetarShowTimedOut,
    );
    for (const layer of metarVectorLayers) {
      map.addLayer(layer, this.beforeLayerId);
      this.layers.push(layer.id);
    }

    const interactiveLayers = INTERACTIVE_LAYERS_NAMES.map((layerName) => `${layerName}_${this.uuid}_tiles`);

    this.setEventListeners(interactiveLayers);
  }

  createTooltip(feature: MapboxGeoJSONFeature): string {
    if (feature.properties && this.displayDateTime) {
      const startDateTime = this.displayDateTime.minus({ hours: METAR_TIME_SPAN });
      const endDateTime = this.displayDateTime;

      apiThreadPool
        .getMetars({
          icao: feature.properties.icao as string,
          startDateTime,
          endDateTime,
        })
        .then((res) => {
          this.updateTooltip(createMetarTooltip(feature.properties, res.metars));
        });
    }

    return createMetarTooltip(feature.properties);
  }

  updateSize(size: number): void {
    for (const [name, { minSize, maxSize }] of Object.entries(ICON_LAYERS)) {
      const newValue = convertRange(
        size,
        [aviationLayerConfig.sizeLimits.min, aviationLayerConfig.sizeLimits.max],
        [minSize, maxSize],
      );
      this.updateMapboxLayoutProperties(`${name}_${this.uuid}_tiles`, { "icon-size": newValue });
    }

    for (const [name, { minSize, maxSize }] of Object.entries(TEXT_LAYERS)) {
      const newValue = convertRange(
        size,
        [aviationLayerConfig.sizeLimits.min, aviationLayerConfig.sizeLimits.max],
        [minSize, maxSize],
      );

      this.updateMapboxLayoutProperties(`${name}_${this.uuid}_tiles`, { "text-size": newValue });
    }
  }

  updateShowTimedOut(showTimedOut: boolean): void {
    for (const [name] of Object.entries(ICON_LAYERS)) {
      const newValue = showTimeOutFeature(showTimedOut);
      this.updateMapboxPaintProperties(`${name}_${this.uuid}_tiles`, { "icon-opacity": newValue });
    }

    for (const [name] of Object.entries(TEXT_LAYERS)) {
      const newValue = showTimeOutFeature(showTimedOut);
      this.updateMapboxPaintProperties(`${name}_${this.uuid}_tiles`, { "text-opacity": newValue });
    }
  }
}
